import { AlertTriangle } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@altek/ui/components/ui/Button";

/**
 * The last line before a white screen.
 *
 * Outermost in the provider chain, because a provider that throws while initialising takes
 * everything below it with it — including any boundary placed inside. Route-level boundaries
 * catch the ordinary case (one page failed); this catches the case where the shell itself did.
 *
 * It does NOT try to recover by re-rendering. A component that threw once during render throws
 * again with the same props, and an automatic retry loop burns the CPU while showing nothing.
 * The user gets a reload, which re-runs the whole tree from a clean state.
 */

interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Monitoring goes here once Sentry is wired. A domain error never reaches this boundary —
    // those are handled where they happen and are product behaviour, not incidents.
    console.error("APP_BOUNDARY", error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="grid min-h-dvh place-items-center bg-bg-app p-6">
        <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 text-center shadow-e1">
          <AlertTriangle size={28} strokeWidth={1.6} className="mx-auto text-danger" aria-hidden="true" />
          <h1 className="mt-3 text-md font-semibold text-fg">Uygulama açılamadı</h1>
          <p className="mt-1 text-sm text-fg-muted">
            Beklenmeyen bir hata oluştu. Sayfayı yenilemek çoğu durumda yeterli olur.
          </p>
          {/**
           * The message is shown in development only. In production it would leak internal
           * vocabulary to somebody who cannot act on it, and the useful identifier for support
           * is the request id on the failed call — not this stack.
           */}
          {import.meta.env.DEV ? (
            <pre className="mt-3 max-h-40 overflow-auto rounded-sm bg-surface-2 p-3 text-left font-mono text-2xs text-fg-muted">
              {error.message}
            </pre>
          ) : null}
          <Button className="mt-4" onClick={() => window.location.reload()}>
            Sayfayı yenile
          </Button>
        </div>
      </div>
    );
  }
}
